import { fabric } from "fabric"
import { debounce } from "lodash-es"
import controls from "./controls"
import Calc from "@/common/util/calculator"
import ObjectMixin from "@/signomatic/app/objects/object_mixin"
import type Sign from "@/signomatic/app/sign"
import { loadImage, getLocation, ie } from "@/signomatic/util"

const stateType = {}

export interface ExtendedCoordinates {
  [key: string]: fabric.Point
  tl: fabric.Point
  mt: fabric.Point
  tr: fabric.Point
  ml: fabric.Point
  mr: fabric.Point
  bl: fabric.Point
  mb: fabric.Point
  br: fabric.Point
  mtr: fabric.Point
  move: fabric.Point
}

export interface ControlObject {
  calcCornerCoords(
    angle: number,
    size: number,
    x: number,
    y: number,
    isTouch: boolean,
    fabricObject?: fabric.Object
  ): void
}

class SignImage extends ObjectMixin(fabric.Image, stateType as SignImageState) {
  oCoords: ExtendedCoordinates

  static async fromState(sign: Sign, state: SignImageState, centerX: number, centerY: number): Promise<SignImage> {
    if (state.src) {
      try {
        const img = await loadImage(state.src, "Anonymous")
        return this.create(img, state, centerX, centerY, sign.minimatic).attachSign(sign).attachState(state)
      } catch (e) {
        console.error("image state URL doesn't load")
        console.error(e)
      }
    }

    const url = this.imageUrl(state, sign.defaultScale, sign.fullData.multicolor)
    const img = await loadImage(url, "Anonymous")
    return this.create(img, state, centerX, centerY).attachSign(sign).attachState(state)
  }

  static create(
    img: HTMLImageElement,
    state: SignImageState,
    centerX: number,
    centerY: number,
    minimatic?: boolean
  ): SignImage {
    const obj = new SignImage(img, {
      scaleX: state.width / (img.naturalWidth || img.width),
      scaleY: state.height / (img.naturalHeight || img.height),
      left: centerX + state.left,
      top: centerY + state.top,
      angle: state.angle,
      flipX: state.mirrored,
      transparentCorners: false,
      cornerSize: 14,
      touchCornerSize: 30,
      fill: state.color,
      originX: "center",
      originY: "center",
      lockScalingFlip: true,
      lockSkewingX: true,
      lockSkewingY: true,
      borderColor: "#0374e3",
      borderScaleFactor: 2,
      cornerColor: "#ffffff",
      cornerStrokeColor: "#0374e3",
      cornerStyle: "circle",
      rotatingPointOffset: 20,
      hasControls: !minimatic,
      lockMovementX: minimatic,
      lockMovementY: minimatic,
      selectable: !minimatic,
      hoverCursor: minimatic ? "cursor" : "grab",
    }).addClass("object", "image")

    obj.controls = controls

    return obj
  }

  static stateToObject(state: SignImageState, obj: SignImage, centerX: number, centerY: number): Partial<SignImage> {
    const newState: Partial<SignImage> = {}

    if (Object.prototype.hasOwnProperty.call(state, "mirrored")) {
      newState.flipX = state.mirrored
    }

    if (state.color) {
      newState.fill = state.color
    }

    if (Object.prototype.hasOwnProperty.call(state, "top")) {
      newState.top = centerY + state.top
    }

    if (Object.prototype.hasOwnProperty.call(state, "left")) {
      newState.left = centerX + state.left
    }

    if (Object.prototype.hasOwnProperty.call(state, "angle")) {
      newState.angle = state.angle
    }

    if (Object.prototype.hasOwnProperty.call(state, "width")) {
      newState.scaleX = (obj.scaleX || 1) / (obj.getScaledWidth() / state.width)
    }

    if (Object.prototype.hasOwnProperty.call(state, "height")) {
      newState.scaleY = (obj.scaleY || 1) / (obj.getScaledHeight() / state.height)
    }

    return newState
  }

  static sourceRatio(scale: number) {
    return Calc.imagePixelRatio() * scale
  }

  static imageUrl(state: SignImageState, scale: number, multicolor: boolean) {
    const mult = this.sourceRatio(scale)
    const color = multicolor && state.colored ? "true" : "false"

    let src = `/image/get?id=${state.id}&width=${Math.round(state.width * mult)}&height=${Math.round(
      state.height * mult
    )}&color=${color}&dpi=${Math.round(Calc.getDPI() * mult)}`

    // Set color params for single-color images
    if (!multicolor || !state.colored) {
      src += "&text_color=" + state.color.replace("#", "")
      src += "&background_color=ffffff"
    }

    return src
  }

  updateImage = debounce(
    () => {
      if (this.isMoving) {
        return
      }

      const url = SignImage.imageUrl(this.state, this.attachedSign.defaultScale, this.attachedSign.fullData.multicolor)

      if (getLocation(this.getElement().src) === url) {
        return
      }

      const img = new Image()
      img.onload = () => {
        this.setElement(img, {
          scaleX: this.state.width / (img.naturalWidth || img.width),
          scaleY: this.state.height / (img.naturalHeight || img.height),
        })

        if (this.attachedSign) {
          this.attachedSign.rerender()
        }
      }

      if (!ie()) {
        img.crossOrigin = "Anonymous"
      }

      img.src = url
    },
    100,
    { leading: false, trailing: true }
  )

  toObject(): SignObjectV2 {
    let border = {}

    if (this.state.border && this.state.border.shape) {
      border = {
        objectBorderType: this.state.border.shape,
        objectBorderFill: this.state.border.color,
        objectBorderColor: this.state.border.borderColor,
        objectBorderSize: this.state.border.size,
        objectBorderThickness: this.state.border.thickness,
      }
    }

    return {
      ...super.toObject(),
      ...border,
      zIndex: this.state.zindex,
      oIndex: this.state.oindex,
      imageId: this.state.id,
      imageName: this.state.name,
      fill: this.state.color,
      flipX: this.state.mirrored,
      colored: this.state.colored,
      side: this.state.side,
    }
  }

  getSvgSrc() {
    const ratio = SignImage.sourceRatio(this.attachedSign.defaultScale)

    let src =
      "/image/get" +
      "?id=" +
      this.state.id +
      "&amp;width=" +
      Math.round((this.width || 1) * (this.scaleX || 1) * ratio) +
      "&amp;height=" +
      Math.round((this.height || 1) * (this.scaleY || 1) * ratio) +
      "&amp;color=" +
      (this.state.colored && this.state.multicolor ? "true" : "false") +
      "&amp;dpi=" +
      Math.round(Calc.getDPI() * ratio)

    // Set color params for single-color images
    if (!this.state.multicolor || !this.state.colored) {
      src += "&amp;text_color=" + this.state.color.replace("#", "")
      src += "&amp;background_color=ffffff"
    }

    return src
  }

  _setCornerCoords(): void {
    const coords = this.oCoords
    const controlKeys = ["tl", "mt", "tr", "ml", "mr", "bl", "mb", "br", "mtr", "move"]

    for (const controlKey of controlKeys) {
      const controlObject = this.controls[controlKey] as ControlObject

      if (!controlObject || !controlObject.calcCornerCoords || !coords) {
        continue
      }

      const coord = coords[controlKey] as any

      if (!coord) {
        continue
      }

      coord.corner = controlObject.calcCornerCoords(
        this.angle || 0,
        this.cornerSize || 0,
        coord.x,
        coord.y,
        false,
        this
      )
      coord.touchCorner = controlObject.calcCornerCoords(
        this.angle || 0,
        this.touchCornerSize || 0,
        coord.x,
        coord.y,
        true,
        this
      )
    }
  }
}

export default SignImage
